import React, { useState } from 'react';
import { Select } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import './HostingPackageTypeForm.css';

const CUSTOM_VALUE = '__custom__';

export interface NumericLimitFieldProps {
  label: string;
  name: string;
  value: number | null;
  options: Array<number | ''>;
  unitSuffix?: string;
  /** Allow a "Custom amount" option that splits the field into a select + textfield row. Defaults true. */
  allowCustom?: boolean;
  onChange: (next: number | null) => void;
}

function isPresetMatch(value: number | null, options: Array<number | ''>): boolean {
  if (value === null) return options.includes('');
  return options.includes(value);
}

function renderOptions(options: Array<number | ''>, unitSuffix: string, allowCustom: boolean) {
  return (
    <>
      {options.map((opt, i) => {
        if (opt === '') {
          return (
            <option key={`unlimited-${i}`} value="">
              Unlimited
            </option>
          );
        }
        return (
          <option key={opt} value={String(opt)}>
            {opt}
            {unitSuffix}
          </option>
        );
      })}
      {allowCustom && <option value={CUSTOM_VALUE}>Custom amount</option>}
    </>
  );
}

export const NumericLimitField: React.FC<NumericLimitFieldProps> = ({
  label,
  name,
  value,
  options,
  unitSuffix = '',
  allowCustom = true,
  onChange,
}) => {
  const [mode, setMode] = useState<'preset' | 'custom'>(
    isPresetMatch(value, options) || !allowCustom ? 'preset' : 'custom'
  );

  // Select onChange: handles both preset selection AND switching modes
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    if (raw === CUSTOM_VALUE) {
      setMode('custom');
      return;
    }
    setMode('preset');
    onChange(raw === '' ? null : Number(raw));
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(null);
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed >= 0 && Number.isInteger(parsed)) {
      onChange(parsed);
    }
  };

  return (
    <div className="hi-ui-numeric-limit-field">
      <FormField label={label} labelPosition="left">
        {mode === 'preset' ? (
          <Select
            name={name}
            value={value === null ? '' : String(value)}
            onChange={handleSelectChange}
            fullWidth
          >
            {renderOptions(options, unitSuffix, allowCustom)}
          </Select>
        ) : (
          <div className="hi-ui-numeric-limit-field__custom-row">
            <Select
              value={CUSTOM_VALUE}
              onChange={handleSelectChange}
              fullWidth
              aria-label={`${label} preset`}
            >
              {renderOptions(options, unitSuffix, allowCustom)}
            </Select>
            <div className="hi-ui-numeric-limit-field__input-unit">
              <Input
                type="number"
                name={name}
                value={value === null ? '' : String(value)}
                onChange={handleCustomChange}
                min={0}
                step={1}
                fullWidth
                placeholder={unitSuffix ? `Number${unitSuffix}` : 'Number'}
              />
              {unitSuffix.trim() && (
                <span className="hi-ui-numeric-limit-field__unit" aria-hidden="true">
                  {unitSuffix.trim()}
                </span>
              )}
            </div>
          </div>
        )}
      </FormField>
    </div>
  );
};
